import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import type { components } from "@/types/api";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export type Requirement = components["schemas"]["RequirementResponse"];
type RequirementCreate = components["schemas"]["RequirementCreate"];
type RequirementUpdate = components["schemas"]["RequirementUpdate"];

export const requirementKeys = {
  forContract: (contractId: string) => ["requirements", contractId] as const,
};

export function useRequirements(contractId: string | undefined) {
  return useQuery({
    queryKey: requirementKeys.forContract(contractId ?? ""),
    enabled: Boolean(contractId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/contracts/{contract_id}/requirements",
        { params: { path: { contract_id: contractId! } } },
      );
      if (error || !data) throw new Error("Failed to load requirements");
      return data;
    },
  });
}

export function useCreateRequirement(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RequirementCreate) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/requirements",
        { params: { path: { contract_id: contractId } }, body },
      );
      if (error || !data) throw new Error("Failed to add requirement");
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: requirementKeys.forContract(contractId) }),
  });
}

export function useUpdateRequirement(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ reqId, body }: { reqId: string; body: RequirementUpdate }) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/contracts/{contract_id}/requirements/{req_id}",
        { params: { path: { contract_id: contractId, req_id: reqId } }, body },
      );
      if (error || !data) throw new Error("Failed to update requirement");
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: requirementKeys.forContract(contractId) }),
  });
}

export function useDeleteRequirement(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reqId: string) => {
      const { error } = await apiClient.DELETE(
        "/api/v1/contracts/{contract_id}/requirements/{req_id}",
        { params: { path: { contract_id: contractId, req_id: reqId } } },
      );
      if (error) throw new Error("Failed to remove requirement");
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: requirementKeys.forContract(contractId) }),
  });
}

export function useSendForSigning(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reqId: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/contracts/{contract_id}/requirements/{req_id}/send-for-signing",
        {
          params: { path: { contract_id: contractId, req_id: reqId } },
          // No overrides — the backend renders the document the requirement maps to
          // (lease vs check-out) and fills the signer from the contract's tenant.
          body: {},
        },
      );
      if (error || !data) throw new Error("Couldn't send for signing");
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: requirementKeys.forContract(contractId) }),
  });
}

export function useUploadRequirementFile(contractId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ reqId, file }: { reqId: string; file: File }) => {
      // Multipart upload via raw fetch (openapi-fetch is JSON-oriented).
      const form = new FormData();
      form.append("file", file);
      const token = useAuthStore.getState().token;
      const res = await fetch(
        `${BASE_URL}/api/v1/contracts/${contractId}/requirements/${reqId}/file`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        },
      );
      if (!res.ok) throw new Error("Upload failed");
      return res.json() as Promise<Requirement>;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: requirementKeys.forContract(contractId) }),
  });
}

/** Opens the requirement's attached file via its presigned URL. */
export async function openRequirementFile(
  contractId: string,
  reqId: string,
): Promise<void> {
  const { data, error } = await apiClient.GET(
    "/api/v1/contracts/{contract_id}/requirements/{req_id}/file",
    { params: { path: { contract_id: contractId, req_id: reqId } } },
  );
  if (error || !data?.url) throw new Error("No file to open");
  window.open(data.url, "_blank", "noopener,noreferrer");
}
